import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Switch, Typography } from "@mui/material";
import {
  RiArrowDownSLine,
  RiAtLine,
  RiCalendarLine,
  RiDraftLine,
  RiGraduationCapLine,
  RiGroupLine,
  RiLinksLine,
  RiMenLine,
  RiMessage2Line,
  RiSearchLine,
  RiSendPlane2Fill,
  RiTimeLine,
  RiWomenLine,
} from "@remixicon/react";
import { announcementStats, getAnnouncement, previewAnnouncementAudience, saveAnnouncement } from "../../services/announcements";
import { getUsersByIds, searchPeople } from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { logUserActivity } from "../../utils/activityLogger";
import { useBreadcrumbName } from "../../components/Layout/BreadcrumbContext";
import { AGE_RANGES, quickPicks } from "../../utils/announcementPresentation";
import {
  BODY_MAX,
  PEOPLE_MAX,
  TITLE_MAX,
  audienceComplete,
  audienceFromForm,
  buildPayload,
  canSubmit,
  confirmText,
  dateInputValue,
  emptyForm,
  formFromAnnouncement,
  insertAtCaret,
  isEditable,
  linkWarning,
  matchLine,
  peopleLabel,
  roleCopy,
  sampleFirstName,
  saveErrorText,
  scheduleWarning,
  sendAtFromForm,
  statusLine,
  successMessage,
  summaryRows,
  timeInputValue,
} from "../../utils/announcementForm";
import { focusRing, srOnly, tokens } from "../../utils/figmaTokens";
import InitialsAvatar from "../../components/kit/InitialsAvatar";
import { StateAction } from "../../components/kit/States";
import { AudienceCard, Card, ChoiceRow, Field, FieldLabel, MatchLine, PersonChip, PhonePreview, PickChip, SectionHead, Segmented, SummaryCard, ToolButton } from "./ComposeParts";

// The New notification page (spec 044 D41–D48), drawn from Figma's Compose screens: the form
// card (audience, content, send settings, the footer) and the side column (the phone preview,
// the audience summary). /notifications/new starts empty; /notifications/<id> loads the
// announcement — a draft or a scheduled one is edited and saved under its id, anything else is
// read-only (D45). The server's callables are the only writers (services/announcements.js).

const GENDERS = [
  { key: "all", label: "All" },
  { key: "male", label: "Male", Icon: RiMenLine },
  { key: "female", label: "Female", Icon: RiWomenLine },
];

// The gender/age switch, 46 × 26 with a 20 px knob (Switch · on 11330:92725).
const switchSx = {
  width: 46,
  height: 26,
  p: 0,
  "& .MuiSwitch-switchBase": { p: "3px", "&.Mui-checked": { transform: "translateX(20px)", color: tokens.brand.on, "& + .MuiSwitch-track": { bgcolor: tokens.brand.primary, opacity: 1 } } },
  "& .MuiSwitch-thumb": { width: 20, height: 20, boxShadow: "none" },
  "& .MuiSwitch-track": { borderRadius: 13, bgcolor: tokens.border.strong, opacity: 1 },
  // disableRipple drops MUI's only focus sign; the keyboard gets a ring round the track.
  "& .MuiSwitch-switchBase.Mui-focusVisible + .MuiSwitch-track": { outline: `2px solid ${tokens.brand.border}`, outlineOffset: "2px" },
};

const labelSx = { fontSize: 14, lineHeight: "22px", color: tokens.text.secondary };

const NotificationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState(emptyForm);
  const [stored, setStored] = useState(null); // the document behind /notifications/<id>
  const [missing, setMissing] = useState(false);
  const [loading, setLoading] = useState(Boolean(id));
  const [opened, setOpened] = useState(undefined); // announcementStats, for a sent one's status line
  const [count, setCount] = useState({ state: "idle" }); // idle | loading | ok | error, with targeted / withDevice
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null); // null: not searching
  const [busy, setBusy] = useState(""); // "" | draft | send | schedule
  const [error, setError] = useState(""); // the footer's line after a failed save
  const [confirm, setConfirm] = useState(null); // null | { state: loading | ready | error, targeted, withDevice, message }
  const lastFocus = useRef("body"); // where "Add user's name" goes: title | body
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const countToken = useRef(0);
  // A new notification is saved as a draft before it is sent or scheduled, and every later save
  // reuses that id: a retry after a lost reply then meets the server's status check ("already
  // being sent") instead of sending everyone a second copy.
  const created = useRef(null);
  const now = new Date(); // each render's clock: the schedule check and the lock screen stay current

  const readOnly = Boolean(stored) && !isEditable(stored);
  const disabled = readOnly || Boolean(busy);
  const copy = roleCopy(form.role);

  // /notifications/<id>: the document, the chosen people's names, and a sent one's opens.
  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setMissing(false);
    try {
      const found = await getAnnouncement(id);
      if (!found) {
        setMissing(true);
        return;
      }
      setStored(found);
      const next = formFromAnnouncement(found);
      if (next.people.length > 0) {
        const users = await getUsersByIds(next.people.map((p) => p.id)).catch(() => ({}));
        next.people = next.people.map((p) => ({ id: p.id, name: users[p.id]?.name || "", email: users[p.id]?.email || "" }));
      }
      setForm(next);
      if (found.status === "sent") {
        announcementStats({ ids: [id] })
          .then((data) => setOpened(data?.opened?.[id]))
          .catch((e) => console.error("Error loading announcement stats:", e));
      }
    } catch (e) {
      console.error("Error loading announcement:", e);
      setMissing(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // The top bar's "Growth / Notifications / <title>" (044 D40); "Not found" once the id is (047 D83).
  useBreadcrumbName(id, stored ? stored.title || "Untitled" : missing ? "Not found" : null);

  // The count (D48): one previewAnnouncementAudience call per settled audience, the newest wins.
  const audience = useMemo(() => audienceFromForm(form), [form]);
  const audienceKey = JSON.stringify(audience);
  const complete = audienceComplete(form);
  const [countRetry, setCountRetry] = useState(0);
  useEffect(() => {
    const token = ++countToken.current;
    if (!complete || readOnly || loading) {
      setCount({ state: "idle" });
      return undefined;
    }
    setCount((c) => ({ ...c, state: "loading" }));
    const timer = setTimeout(async () => {
      try {
        const data = await previewAnnouncementAudience({ audience: JSON.parse(audienceKey) });
        if (token === countToken.current) setCount({ state: "ok", targeted: data?.targeted ?? 0, withDevice: data?.withDevice ?? 0 });
      } catch (e) {
        console.error("Error counting the audience:", e);
        if (token === countToken.current) setCount({ state: "error" });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [audienceKey, complete, readOnly, loading, countRetry]);

  // The people search (D41): as you type, from two characters, the chosen role only, never
  // someone already chosen.
  const chosenIds = form.people.map((p) => p.id).join(",");
  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) {
      setResults(null);
      return undefined;
    }
    let stale = false;
    const timer = setTimeout(async () => {
      try {
        const people = await searchPeople(term, { role: form.role });
        if (stale) return;
        const chosen = new Set(chosenIds.split(","));
        setResults(people.filter((p) => !chosen.has(p.id)));
      } catch (e) {
        console.error("Error searching people:", e);
        if (!stale) setResults([]);
      }
    }, 300);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [search, form.role, chosenIds]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  // Switching role drops the chosen people and the age range (D41).
  const setRole = (role) => role !== form.role && set({ role, people: [], age: "all" });
  const addPerson = (p) => {
    if (form.people.length >= PEOPLE_MAX) return;
    set({ people: [...form.people, { id: p.id, name: p.name || "", email: p.email || "" }] });
    setSearch("");
  };
  const removePerson = (pid) => set({ people: form.people.filter((p) => p.id !== pid) });
  const pick = (at) => set({ date: dateInputValue(at), time: timeInputValue(at) });

  // "{name}" at the caret of the field last focused, within its limit (D43).
  const insertName = () => {
    const target = lastFocus.current;
    const ref = target === "title" ? titleRef : bodyRef;
    const el = ref.current;
    const { text, caret, inserted } = insertAtCaret(form[target], el?.selectionStart, target === "title" ? TITLE_MAX : BODY_MAX);
    if (!inserted) return;
    set({ [target]: text });
    // After React writes the value, put the caret after the token.
    setTimeout(() => {
      if (!ref.current) return;
      ref.current.focus();
      ref.current.setSelectionRange(caret, caret);
    }, 0);
  };

  const actor = () => ({
    userId: user?.uid || user?.id || "unknown",
    userName: user?.name || user?.displayName || "Unknown User",
    userEmail: user?.email || "unknown@example.com",
  });

  const saveDraft = async () => {
    setBusy("draft");
    setError("");
    try {
      await saveAnnouncement(buildPayload(form, "draft", id || created.current));
      navigate("/notifications", { state: successMessage("draft", form) });
    } catch (e) {
      console.error("Error saving draft:", e);
      setError(saveErrorText(e, { saved: Boolean(created.current) }));
      setBusy("");
    }
  };

  // The primary button: a fresh count first, then one question (D48).
  const openConfirm = async () => {
    setError("");
    setConfirm({ state: "loading" });
    try {
      const data = await previewAnnouncementAudience({ audience });
      setConfirm({ state: "ready", targeted: data?.targeted ?? 0, withDevice: data?.withDevice ?? 0 });
    } catch (e) {
      console.error("Error counting the audience:", e);
      setConfirm({ state: "error", message: e.message || "Could not count the audience." });
    }
  };

  const submit = async () => {
    const action = form.timing === "schedule" ? "schedule" : "send";
    setBusy(action);
    try {
      if (!id && !created.current) created.current = (await saveAnnouncement(buildPayload(form, "draft"))).id;
      const target = id || created.current;
      const result = await saveAnnouncement(buildPayload(form, action, target));
      // The log row is best effort: an accountant's write is denied by the rules, and
      // logUserActivity swallows that, so the send never looks failed for it (R9).
      await logUserActivity({
        ...actor(),
        action: action === "send" ? "SEND_ANNOUNCEMENT" : "SCHEDULE_ANNOUNCEMENT",
        entityType: "announcement",
        entityId: result?.id || target,
        entityName: form.title.trim(),
        after: { status: result?.status || null, audience, counts: result?.counts || null, sendAt: sendAtFromForm(form)?.toISOString() || null },
        description: `${user?.name || "User"} ${action === "send" ? "sent" : "scheduled"} announcement: ${form.title.trim()}`,
      });
      navigate("/notifications", { state: successMessage(action, form, result) });
    } catch (e) {
      console.error("Error sending announcement:", e);
      setConfirm(null);
      setError(saveErrorText(e, { saved: Boolean(created.current) }));
      setBusy("");
    }
  };

  if (missing) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          Notification not found.
        </Alert>
        <Button component={RouterLink} to="/notifications" sx={{ mt: 2 }}>
          Back to history
        </Button>
      </Box>
    );
  }
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const countValue = count.state === "ok" ? count.targeted : count.state === "error" ? "—" : count.state === "loading" ? "…" : "—";
  const recipients = complete ? peopleLabel(countValue, form.role) : "—";
  const retry = count.state === "error" && (
    <StateAction outlined onClick={() => setCountRetry((n) => n + 1)}>
      Retry
    </StateAction>
  );
  const warning = scheduleWarning(form, now);
  const badLink = linkWarning(form);
  const ready = !disabled && canSubmit(form, now);
  const primaryLabel = form.timing === "schedule" ? "Schedule notification" : "Send notification";
  const busyLabel = busy === "send" ? "Sending…" : busy === "schedule" ? "Scheduling…" : primaryLabel;
  const arrivesAt = (form.timing === "schedule" && sendAtFromForm(form)) || now;
  const dialogText = confirm?.state === "ready" ? confirmText(form, confirm) : null;

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) 370px" }, gap: "16px", alignItems: "start" }}>
      <Typography component="h1" sx={srOnly}>
        {id ? stored?.title || "Notification" : "New notification"}
      </Typography>

      {/* The form card */}
      <Box component="form" onSubmit={(e) => e.preventDefault()} sx={{ bgcolor: tokens.surface.default, border: `1px solid ${tokens.border.default}`, borderRadius: "20px", p: "24px", display: "flex", flexDirection: "column", gap: "22px", minWidth: 0 }}>
        {readOnly && (
          <Alert severity={stored.status === "failed" ? "error" : "info"} sx={{ borderRadius: "12px" }}>
            {statusLine(stored, opened)}
          </Alert>
        )}

        {/* 1 · Choose the audience (D41) */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <SectionHead large title="Choose the audience" subtitle="Specify who will receive this notification" />
          <Box role="radiogroup" aria-label="Audience" sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <AudienceCard Icon={RiGroupLine} title="Students" subtitle="Send notification to students" selected={form.role === "student"} onClick={() => setRole("student")} disabled={disabled} />
            <AudienceCard Icon={RiGraduationCapLine} title="Teachers" subtitle="Send notification to teachers" selected={form.role === "teacher"} onClick={() => setRole("teacher")} disabled={disabled} />
          </Box>
          <Card>
            <SectionHead title={copy.choose} subtitle={copy.chooseSub} />
            <Box role="radiogroup" aria-label={copy.choose} sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <ChoiceRow title={copy.all} subtitle={copy.allSub} selected={form.kind === "all"} onClick={() => set({ kind: "all" })} disabled={disabled} />
              <ChoiceRow title={copy.specific} subtitle={copy.specificSub} selected={form.kind === "specific"} onClick={() => set({ kind: "specific" })} disabled={disabled} />
            </Box>
            <Box sx={{ height: "1px", bgcolor: tokens.border.subtle }} />

            {form.kind === "all" ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Typography component="label" htmlFor="notification-filter" sx={{ flex: 1, fontSize: 16, lineHeight: "26px", fontWeight: 500, color: tokens.text.primary }}>
                    {copy.filter}
                  </Typography>
                  <Switch id="notification-filter" checked={form.filterOn} onChange={(e) => set({ filterOn: e.target.checked })} disabled={disabled} disableRipple sx={switchSx} />
                </Box>
                {form.filterOn && (
                  <>
                    <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                      <Box sx={{ flex: "1 1 280px", display: "flex", flexDirection: "column", gap: "8px" }}>
                        <Typography sx={labelSx}>Gender</Typography>
                        <Segmented label="Gender" options={GENDERS} value={form.gender} onChange={(gender) => set({ gender })} disabled={disabled} />
                      </Box>
                      {form.role === "student" && (
                        <Box sx={{ flex: "1 1 280px", display: "flex", flexDirection: "column", gap: "8px" }}>
                          <FieldLabel id="notification-age" label="Age range" strong={false} />
                          <Box sx={{ position: "relative", display: "flex", alignItems: "center", borderRadius: "12px", boxShadow: `inset 0 0 0 1px ${tokens.border.default}`, "&:focus-within": { boxShadow: `inset 0 0 0 1.5px ${tokens.buttonBorder}` } }}>
                            <Box
                              component="select"
                              id="notification-age"
                              value={form.age}
                              onChange={(e) => set({ age: e.target.value })}
                              disabled={disabled}
                              sx={{ appearance: "none", width: "100%", border: 0, outline: 0, bgcolor: "transparent", px: "14px", py: "12px", pr: "42px", borderRadius: "12px", fontFamily: "inherit", fontSize: 14, lineHeight: "22px", color: tokens.text.primary, cursor: disabled ? "default" : "pointer" }}
                            >
                              {AGE_RANGES.map((r) => (
                                <option key={r.key} value={r.key}>
                                  {r.label}
                                </option>
                              ))}
                            </Box>
                            <Box sx={{ position: "absolute", right: 14, display: "flex", color: tokens.icon.default, pointerEvents: "none" }}>
                              <RiArrowDownSLine size={18} />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <MatchLine action={retry}>{matchLine(countValue, form)}</MatchLine>
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <Box sx={{ position: "relative" }}>
                  <Field
                    Icon={RiSearchLine}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Escape" && setSearch("")}
                    placeholder={copy.searchPlaceholder}
                    inputProps={{ "aria-label": copy.searchPlaceholder, autoComplete: "off" }}
                    disabled={disabled || form.people.length >= PEOPLE_MAX}
                  />
                  {results && (
                    <Box role="listbox" aria-label="People found" sx={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 2, maxHeight: 280, overflowY: "auto", bgcolor: tokens.surface.default, border: `1px solid ${tokens.border.default}`, borderRadius: "12px", boxShadow: tokens.shadow.menu, p: "6px" }}>
                      {results.length === 0 ? (
                        <Typography sx={{ ...labelSx, p: "10px 12px" }}>No {copy.one} matches "{search.trim()}".</Typography>
                      ) : (
                        results.map((p) => (
                          <ButtonBase key={p.id} role="option" aria-selected={false} onClick={() => addPerson(p)} disableRipple sx={{ width: "100%", justifyContent: "flex-start", gap: "10px", p: "8px 10px", borderRadius: "8px", textAlign: "left", "&:hover": { bgcolor: tokens.hover }, ...focusRing }}>
                            <InitialsAvatar name={p.name || p.email || "?"} colorKey={p.id} size={32} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
                                {p.name || "Unknown"}
                              </Typography>
                              <Typography variant="labelMedium" component="div" noWrap sx={{ color: tokens.text.tertiary }}>
                                {p.email || p.phone || "No contact"}
                              </Typography>
                            </Box>
                          </ButtonBase>
                        ))
                      )}
                    </Box>
                  )}
                </Box>
                {form.people.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    {form.people.map((p) => (
                      <PersonChip key={p.id} person={p} onRemove={() => removePerson(p.id)} disabled={disabled} />
                    ))}
                  </Box>
                )}
                {form.people.length >= PEOPLE_MAX && <Typography sx={labelSx}>Up to {PEOPLE_MAX} people can be chosen.</Typography>}
              </Box>
            )}
          </Card>
        </Box>

        {/* 2 · Notice content (D43) */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <SectionHead title="Notice content" subtitle="Write the title of the notification and the message you want to send." />
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <FieldLabel id="notification-title" label="Title" counter={`${form.title.length} / ${TITLE_MAX}`} />
            <Field
              large
              id="notification-title"
              inputRef={titleRef}
              value={form.title}
              onChange={(e) => set({ title: e.target.value.slice(0, TITLE_MAX) })}
              onFocus={() => (lastFocus.current = "title")}
              inputProps={{ maxLength: TITLE_MAX }}
              placeholder="Your Ramadan schedule is ready"
              disabled={disabled}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <FieldLabel id="notification-body" label="Message" counter={`${form.body.length} / ${BODY_MAX}`} />
            <Field
              large
              multiline
              minRows={3}
              id="notification-body"
              inputRef={bodyRef}
              value={form.body}
              onChange={(e) => set({ body: e.target.value.slice(0, BODY_MAX) })}
              onFocus={() => (lastFocus.current = "body")}
              inputProps={{ maxLength: BODY_MAX }}
              placeholder="Peace, mercy, and blessings of God be upon you."
              EndIcon={RiMessage2Line}
              disabled={disabled}
            />
          </Box>
          {form.linkOn && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <FieldLabel id="notification-url" label="Link" />
              <Field Icon={RiLinksLine} id="notification-url" type="url" value={form.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://…" disabled={disabled} />
              {badLink && (
                <Typography sx={{ ...labelSx, color: tokens.error.ink }} role="alert">
                  {badLink}
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
            <ToolButton Icon={RiAtLine} onClick={insertName} disabled={disabled}>
              Add user's name
            </ToolButton>
            <ToolButton Icon={RiLinksLine} onClick={() => set({ linkOn: !form.linkOn })} pressed={form.linkOn} disabled={disabled}>
              Add link
            </ToolButton>
          </Box>
        </Box>

        {/* 3 · Send settings (D44) */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <SectionHead title="Send settings" subtitle="Specify the sending time and additional options" />
          <Box role="radiogroup" aria-label="Send time" sx={{ display: "flex", gap: "16px", flexWrap: "wrap", "& > *": { flex: "1 1 280px" } }}>
            <ChoiceRow strong title="Send now" subtitle="Goes out once confirmed" selected={form.timing === "now"} onClick={() => set({ timing: "now" })} disabled={disabled} />
            <ChoiceRow strong title="Schedule for later" subtitle="Pick a date and time" selected={form.timing === "schedule"} onClick={() => set({ timing: "schedule" })} disabled={disabled} />
          </Box>
        </Box>
        {form.timing === "schedule" && (
          <Card sx={{ borderRadius: "14px", p: "16px" }}>
            <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", "& > *": { flex: "1 1 220px" } }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <FieldLabel id="notification-date" label="Date" strong={false} />
                <Field Icon={RiCalendarLine} id="notification-date" type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} inputProps={{ min: dateInputValue(now) }} disabled={disabled} />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <FieldLabel id="notification-time" label="Time" strong={false} />
                <Field Icon={RiTimeLine} id="notification-time" type="time" value={form.time} onChange={(e) => set({ time: e.target.value })} disabled={disabled} />
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography sx={labelSx}>Quick picks</Typography>
              <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {quickPicks(now).map((p) => (
                  <PickChip key={p.label} onClick={() => pick(p.at)} disabled={disabled}>
                    {p.label}
                  </PickChip>
                ))}
              </Box>
            </Box>
            {warning && !readOnly && (
              <Typography sx={{ ...labelSx, color: tokens.warning.ink }} role="alert">
                {warning}
              </Typography>
            )}
          </Card>
        )}

        {/* Footer actions */}
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "14px", pt: "6px", flexWrap: "wrap" }}>
          {readOnly ? (
            <Button size="large" variant="outlined" component={RouterLink} to="/notifications">
              Back to history
            </Button>
          ) : (
            <>
              <Button size="large" variant="outlined" onClick={saveDraft} disabled={disabled} endIcon={<RiDraftLine size={20} />}>
                {busy === "draft" ? "Saving…" : "Save as draft"}
              </Button>
              <Button size="large" variant="contained" onClick={openConfirm} disabled={!ready} endIcon={busy && busy !== "draft" ? <CircularProgress size={18} color="inherit" /> : <RiSendPlane2Fill size={20} />}>
                {busyLabel}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* The side column */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
        <PhonePreview line={copy.previewLine} title={form.title} body={form.body} at={arrivesAt} first={sampleFirstName(form.people)} />
        <SummaryCard rows={summaryRows(form)} recipients={recipients} action={retry} />
      </Box>

      {/* Admin · Confirm Dialog 10708:8947: the fresh count, then one question (D48). */}
      <Dialog open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{confirm?.state === "ready" ? dialogText.title : confirm?.state === "error" ? "Could not count the audience" : "Counting the audience…"}</DialogTitle>
        <DialogContent dividers>
          {confirm?.state === "loading" ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <DialogContentText>{confirm?.state === "ready" ? dialogText.message : confirm?.message}</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={Boolean(busy)}>
            Cancel
          </Button>
          {confirm?.state === "error" ? (
            <Button variant="contained" onClick={openConfirm}>
              Retry
            </Button>
          ) : (
            <Button variant="contained" onClick={submit} disabled={confirm?.state !== "ready" || Boolean(dialogText?.blocked) || Boolean(busy)}>
              {busy && busy !== "draft" ? busyLabel : form.timing === "schedule" ? "Schedule" : "Send"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationPage;
